import { useEffect, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Typography,
  alpha,
} from '@mui/material';
import { MoreVertical, Sparkles } from 'lucide-react';

import type { AppraisalGraphQuery } from '@graphql/__generated__/graphql';
import AlertPropertyAppraisable from '@modules/_shared/components/AlertPropertyAppraisable';
import { AppraisalHistoryDialog } from '@modules/_shared/components/appraisal/AppraisalHistoryDialog';
import {
  GET_APPRAISAL_HISTORY,
  GET_PROPERTY_APPRAISABLE,
} from '@modules/_shared/components/appraisal/appraisalQueries';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { usePropertyAppraisal } from '@modules/_shared/hooks/useAppraiseProperty';
import {
  formatAppraisalPrice,
  hasValidAppraisalData,
} from '@modules/_shared/utils/formatting';
import { AppraisalGraph } from '@modules/leads/AppraisalGraph';
import { useLocale } from '@shared/hooks/locale';
import { getCurrencyByCountryCode } from '@shared/LocaleContext';

type ListingPriceAppraisalPanelProps = {
  propertyId: string;
  latestAppraisalId?: string | null;
  countryCode?: string | null;
  canEdit: boolean;
  onAppraised?: () => Promise<unknown> | void;
};

export const ListingPriceAppraisalPanel = ({
  propertyId,
  latestAppraisalId,
  countryCode,
  canEdit,
  onAppraised,
}: ListingPriceAppraisalPanelProps) => {
  const { t, locale } = useLocale();
  const currency = getCurrencyByCountryCode(countryCode ?? 'CH');

  const [showGraph, setShowGraph] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [error, setError] = useState<ErrorLike | null>(null);
  const [appraisedData, setAppraisedData] =
    useState<AppraisalGraphQuery['appraisals_by_pk']>(null);
  const [isAppraising, setIsAppraising] = useState(false);

  const {
    data: appraisableData,
    loading: appraisableLoading,
    error: appraisableError,
  } = useQuery(GET_PROPERTY_APPRAISABLE, {
    variables: { propertyId },
    skip: propertyId === '' || !canEdit,
  });

  const {
    data: historyData,
    loading: historyLoading,
    refetch: refetchHistory,
  } = useQuery(GET_APPRAISAL_HISTORY, {
    variables: { propertyId },
    skip: propertyId === '' || !historyOpen,
  });

  const {
    appraiseProperty,
    appraisalData,
    queryLoading,
    queryError,
    mutationError,
  } = usePropertyAppraisal(latestAppraisalId ?? undefined);

  useEffect(() => {
    setError(appraisableError ?? queryError ?? mutationError ?? null);
  }, [appraisableError, queryError, mutationError]);

  useEffect(() => {
    setAppraisedData(null);
  }, [propertyId]);

  useEffect(() => {
    if (appraisedData?.id != null && appraisalData?.id === appraisedData.id) {
      setAppraisedData(null);
    }
  }, [appraisalData?.id, appraisedData?.id]);

  const appraisable = appraisableData?.property_appraisable?.appraisable;
  const appraisableErrors = appraisable?.errors ?? [];
  const visibleAppraisalData = appraisedData ?? appraisalData;

  const hasOnlineValuation = hasValidAppraisalData(visibleAppraisalData);
  const isLoadingExistingValuation =
    latestAppraisalId != null && visibleAppraisalData == null && queryLoading;

  const canTriggerValuation =
    propertyId !== '' && (appraisable?.result ?? false) && canEdit;

  const showMissingFields =
    !hasOnlineValuation &&
    !appraisableLoading &&
    appraisableErrors.length > 0 &&
    canEdit;

  const handleAppraise = async () => {
    if (!canTriggerValuation || isAppraising) {
      return;
    }
    setIsAppraising(true);
    try {
      const newAppraisalData = await appraiseProperty(propertyId);
      if (newAppraisalData != null) {
        setAppraisedData(newAppraisalData);
      }
      await onAppraised?.();
      if (historyOpen) {
        await refetchHistory();
      }
    } catch (appraiseError) {
      setError(appraiseError as ErrorLike);
    } finally {
      setIsAppraising(false);
    }
  };

  return (
    <Box>
      <Typography
        variant="overline"
        sx={{ color: 'text.secondary', letterSpacing: 0.5 }}
      >
        {t('Property valuation')}
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          mt: 0.5,
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: theme =>
            (theme.vars ?? theme).palette.background.paper,
        }}
      >
        {hasOnlineValuation ? (
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '1.0625rem',
                    fontWeight: 700,
                    lineHeight: 1.3,
                  }}
                >
                  {formatAppraisalPrice(visibleAppraisalData, currency, locale)}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('Online valuation')}
                  {visibleAppraisalData?.updated_at != null && (
                    <>
                      {' '}
                      <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                        •{' '}
                        <TimeAgo addSuffix strict={false}>
                          {visibleAppraisalData.updated_at}
                        </TimeAgo>
                      </Box>
                    </>
                  )}
                </Typography>
              </Box>
              <IconButton
                size="small"
                aria-label={t('Actions')}
                onClick={event => setMenuAnchor(event.currentTarget)}
                sx={{ flexShrink: 0 }}
              >
                <MoreVertical size={18} aria-hidden="true" />
              </IconButton>
            </Box>

            <Collapse in={showGraph} unmountOnExit>
              {visibleAppraisalData?.id != null && (
                <Box sx={{ mt: 2 }}>
                  <AppraisalGraph
                    latestAppraisalId={visibleAppraisalData.id}
                    propertyId={propertyId}
                    canAppraise={false}
                  />
                </Box>
              )}
            </Collapse>

            {canTriggerValuation && (
              <Button
                fullWidth
                variant="outlined"
                loading={isAppraising}
                loadingPosition="start"
                startIcon={<Sparkles size={16} />}
                onClick={handleAppraise}
                sx={theme => ({
                  mt: 2,
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  color: (theme.vars ?? theme).palette.primary.main,
                  '&:hover': {
                    borderColor: alpha(theme.palette.primary.main, 0.5),
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                  },
                })}
              >
                {t('Reappraise')}
              </Button>
            )}
          </Box>
        ) : isLoadingExistingValuation ? (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box
            sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {canEdit
                ? t(
                    'No valuation yet. Click "Appraise" to estimate the value of this property.',
                  )
                : t('No valuation yet.')}
            </Typography>
            {appraisableLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {showMissingFields && (
              <AlertPropertyAppraisable errors={appraisableErrors} />
            )}
            {canEdit && (
              <Button
                variant="contained"
                loading={isAppraising}
                loadingPosition="start"
                startIcon={<Sparkles size={16} />}
                onClick={handleAppraise}
                disabled={!canTriggerValuation}
                fullWidth
              >
                {t('Appraise')}
              </Button>
            )}
          </Box>
        )}
      </Paper>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setShowGraph(prev => !prev);
          }}
        >
          {showGraph ? t('Hide graph') : t('Show graph')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setHistoryOpen(true);
          }}
        >
          {t('View history')}
        </MenuItem>
      </Menu>

      <AppraisalHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        loading={historyLoading}
        appraisals={historyData?.appraisals ?? []}
        currency={currency}
        locale={locale}
      />

      <MutationErrorSnackbar error={error} onClose={() => setError(null)} />
    </Box>
  );
};
